/** Supported health states reported by a managed node. */
export type ReportedHealth = "healthy" | "warning" | "critical";

/** Derived fleet state, including loss-of-contact conditions. */
export type NodeStatus = ReportedHealth | "stale" | "offline";

/** Backup verification state exposed by the read-only plane. */
export type BackupState =
  | { state: "never-verified" }
  | { state: "verified"; verifiedAt: string }
  | { state: "failed"; attemptedAt: string };

/** Update-readiness state. The dashboard never initiates updates. */
export type UpdateState =
  | { state: "current"; latestRelease: string }
  | { state: "available"; latestRelease: string }
  | { state: "blocked"; latestRelease: string; reason: string };

/** Sanitized latest state for one enrolled appliance. */
export interface FleetNode {
  readonly id: string;
  /** Null for a physical host; a guest references its approved host identity. */
  readonly parentNodeId?: string | null;
  readonly name: string;
  readonly architecture: string;
  readonly release: string;
  readonly lifecycle: string;
  readonly lastSeenAt: string;
  readonly lastSequence: number;
  /** Guest inventory reported by this physical Proxmox host, not Fleet identities. */
  readonly localGuestCount?: number | null;
  /** How the host came under management; null until a v1.9.0 agent reports it. */
  readonly lifecycleMode?: "create" | "adopt" | null;
  /** Other active identities reporting the same physical machine; non-empty means a duplicate. */
  readonly duplicateOf?: readonly string[];
  readonly health: ReportedHealth;
  readonly diskUsedPercent: number;
  readonly memoryUsedPercent: number | null;
  readonly backup: BackupState;
  readonly update: UpdateState;
  readonly tags?: readonly string[];
  readonly projectIds?: readonly string[];
  readonly projectNames?: readonly string[];
  readonly locations?: readonly string[];
  readonly capabilities?: readonly string[];
}

/** Privacy policy controlling whether project coordinates leave the encrypted record. */
export type ProjectMapPolicy = "exact" | "approximate" | "hidden";

/** Administrator-managed grouping and encrypted customer/location metadata. */
export interface Project {
  readonly id: string;
  readonly name: string;
  readonly customerName: string;
  readonly contactName: string;
  readonly email: string;
  readonly phone: string;
  readonly location: string;
  readonly address: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly notes: string;
  readonly mapPolicy?: ProjectMapPolicy;
  readonly nodeIds: readonly string[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Editable project payload accepted by the authenticated administration API. */
export type ProjectDraft = Omit<Project, "id" | "createdAt" | "updatedAt"> & {
  readonly preserveCoordinates?: boolean;
};

/** Closed, URL-serializable fleet filter grammar. */
export interface FleetFilter {
  readonly query: string;
  readonly statuses: readonly NodeStatus[];
  readonly tags: readonly string[];
  readonly projectIds?: readonly string[];
  readonly locations?: readonly string[];
  readonly releases?: readonly string[];
  readonly capabilities?: readonly string[];
  readonly maxContactAgeMinutes?: number | null;
}

/** Saved operator view containing only validated filter and presentation fields. */
export type FleetColumn =
  | "status"
  | "name"
  | "project"
  | "location"
  | "release"
  | "disk"
  | "memory"
  | "backup"
  | "update"
  | "last-seen";

/** Saved operator view containing only validated filter and presentation fields. */
export interface FleetView {
  readonly id: string;
  readonly name: string;
  readonly filter: FleetFilter;
  readonly sort: "name" | "status" | "last-seen";
  readonly columns: readonly FleetColumn[];
  readonly groupBy: "none" | "project" | "status" | "location";
  readonly isDefault: boolean;
}

/** Explicit scope shared by maintenance and alert policy. */
export type FleetScope =
  | { readonly type: "fleet" }
  | { readonly type: "project"; readonly projectId: string }
  | { readonly type: "node"; readonly nodeId: string };

/** Maintenance additionally supports one logical window targeting a bounded node set. */
export type MaintenanceScope = FleetScope | {
  readonly type: "nodes";
  readonly nodeIds: readonly string[];
};

/** Planned or cancelled period during which alerts are suppressed, not discarded. */
export interface MaintenanceWindow {
  readonly id: string;
  readonly scope: MaintenanceScope;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly timezone: string;
  readonly reason: string;
  readonly state: "scheduled" | "cancelled";
}

/** Bounded metrics supported by the read-only alert evaluator. */
export type AlertMetric =
  | "contact-age"
  | "cpu"
  | "memory"
  | "disk"
  | "container-health"
  | "restart-count"
  | "backup-age"
  | "version-compliance";

/** Metric-specific unit contract, discriminated by the persisted metric name. */
export type AlertCondition =
  | {
    readonly metric: "cpu" | "memory" | "disk";
    readonly unit: "percent";
    readonly comparison: "gt" | "gte" | "lt" | "lte" | "eq";
    readonly threshold: number;
    readonly hysteresis: number;
  }
  | {
    readonly metric: "contact-age";
    readonly unit: "minutes";
    readonly comparison: "gt" | "gte" | "lt" | "lte" | "eq";
    readonly threshold: number;
    readonly hysteresis: number;
  }
  | {
    readonly metric: "backup-age";
    readonly unit: "days";
    readonly comparison: "gt" | "gte" | "lt" | "lte" | "eq";
    readonly threshold: number;
    readonly hysteresis: number;
  }
  | {
    readonly metric:
      | "container-health"
      | "restart-count"
      | "version-compliance";
    readonly unit: "count";
    readonly comparison: "gt" | "gte" | "lt" | "lte" | "eq";
    readonly threshold: number;
    readonly hysteresis: number;
  };

/** Provider-neutral notification route containing a secret reference, never a secret value. */
export type NotificationRoute = { readonly type: "none" } | {
  readonly type: "ntfy";
  readonly secretRef: "NTFY_URL";
};

/** Return the only valid display/evaluation unit for a metric. */
export function alertUnit(metric: AlertMetric): AlertCondition["unit"] {
  if (metric === "cpu" || metric === "memory" || metric === "disk") {
    return "percent";
  }
  if (metric === "contact-age") return "minutes";
  if (metric === "backup-age") return "days";
  return "count";
}

/** Construct a metric-discriminated condition using its canonical unit. */
export function createAlertCondition(
  metric: AlertMetric,
  comparison: AlertCondition["comparison"],
  threshold: number,
  hysteresis: number,
): AlertCondition {
  if (metric === "cpu" || metric === "memory" || metric === "disk") {
    return { metric, unit: "percent", comparison, threshold, hysteresis };
  }
  if (metric === "contact-age") {
    return { metric, unit: "minutes", comparison, threshold, hysteresis };
  }
  if (metric === "backup-age") {
    return { metric, unit: "days", comparison, threshold, hysteresis };
  }
  return { metric, unit: "count", comparison, threshold, hysteresis };
}

/** Administrator-authored alert policy; it can notify but cannot mutate a node. */
export type AlertRule = AlertCondition & {
  readonly id: string;
  readonly name: string;
  readonly durationMinutes: number;
  readonly cooldownMinutes: number;
  readonly severity: "warning" | "critical";
  readonly scope: FleetScope;
  readonly notificationRoute: NotificationRoute;
  readonly enabled: boolean;
};

/** Sanitized container observation reported by a node-local read-only collector. */
export interface ServiceObservation {
  readonly catalogId: string;
  readonly name: string;
  readonly state: "running" | "stopped" | "restarting" | "unknown";
  readonly health: "healthy" | "unhealthy" | "starting" | "none";
  readonly restartCount: number;
  readonly image: string;
  readonly dependencies: readonly string[];
  readonly observedAt: string;
}

/** Aggregate counts shown in the fleet overview. */
export interface FleetSummary {
  readonly total: number;
  readonly healthy: number;
  readonly warning: number;
  readonly critical: number;
  readonly stale: number;
  readonly offline: number;
}

const STALE_AFTER_MS = 15 * 60 * 1_000;
const OFFLINE_AFTER_MS = 60 * 60 * 1_000;

/** Derive contact-aware node status from its last trusted observation. */
export function classifyNode(node: FleetNode, now: Date): NodeStatus {
  const observedAt = Date.parse(node.lastSeenAt);
  const age = now.getTime() - observedAt;
  if (!Number.isFinite(observedAt) || age >= OFFLINE_AFTER_MS) return "offline";
  if (age >= STALE_AFTER_MS) return "stale";
  return node.health;
}

/** Return independently counted physical hosts, excluding attached guests. */
export function physicalNodes(nodes: readonly FleetNode[]): FleetNode[] {
  return nodes.filter((node) => !node.parentNodeId);
}

/** Identify guests whose approved physical host is missing from the active inventory. */
export function orphanedGuests(nodes: readonly FleetNode[]): FleetNode[] {
  const hosts = new Set(physicalNodes(nodes).map((node) => node.id));
  return nodes.filter((node) =>
    node.parentNodeId && !hosts.has(node.parentNodeId)
  );
}

/** Roll up guest faults without counting a VM as another physical PC. */
export function physicalNodeStatus(node: FleetNode, nodes: readonly FleetNode[], now: Date): NodeStatus {
  const hostStatus = classifyNode(node, now);
  if (hostStatus === "offline" || hostStatus === "critical") return hostStatus;
  const guests = nodes.filter((guest) => guest.parentNodeId === node.id);
  const guestStatuses = guests.map((guest) => classifyNode(guest, now));
  if (guestStatuses.some((status) => status === "critical" || status === "offline")) return "critical";
  if (hostStatus === "warning" || hostStatus === "stale" || guestStatuses.some((status) => status !== "healthy")) return "warning";
  return "healthy";
}

/** Count physical hosts once while reflecting the health of attached guests. */
export function summarizeFleet(
  nodes: readonly FleetNode[],
  now: Date,
): FleetSummary {
  const summary = {
    total: physicalNodes(nodes).length,
    healthy: 0,
    warning: 0,
    critical: 0,
    stale: 0,
    offline: 0,
  };
  for (const node of physicalNodes(nodes)) summary[physicalNodeStatus(node, nodes, now)] += 1;
  return summary;
}
